import React from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  History as HistoryIcon,
  CloudSync as CloudSyncIcon,
  Storage as StorageIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUserPermissions } from '../../hooks/useUserPermissions';

/**
 * Accounting Admin Page
 * Main dashboard for accounting administration functions
 */
export const AccountingAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useUserPermissions();

  // Check if user has admin-level permissions
  const hasAdminPermission = hasPermission('accounting.admin') || hasPermission('system.admin');

  const adminSections = [
    {
      title: 'Vendor Management',
      description: 'View QuickBooks vendor information',
      icon: <AccountBalanceIcon fontSize="large" />,
      action: () => navigate('/admin/hub/accounting/vendors'),
      color: '#7b1fa2',
    },
    {
      title: 'Invoice Management',
      description: 'View and manage all invoices across vendors',
      icon: <ReceiptIcon fontSize="large" />,
      action: () => navigate('/admin/hub/accounting/invoices'),
      color: '#388e3c',
    },
    {
      title: 'Bill Management',
      description: 'View and manage all bills across vendors',
      icon: <DescriptionIcon fontSize="large" />,
      action: () => navigate('/admin/hub/accounting/bills'),
      color: '#f57c00',
    },
    {
      title: 'Invoice/Bill Management',
      description: 'View invoices and bills with their relationships across all vendors',
      icon: <AssignmentIcon fontSize="large" />,
      action: () => navigate('/admin/hub/accounting/invoice-bills'),
      color: '#1976d2',
    },
    ...(hasAdminPermission ? [{
      title: 'QuickBooks Integration',
      description: 'Manage QuickBooks connection, data synchronization, and sync settings',
      icon: <CloudSyncIcon fontSize="large" />,
      action: () => navigate('/admin/quickbooks'),
      color: '#00695c',
    },
    {
      title: 'QuickBooks Tables',
      description: 'Browse and view all synchronized QuickBooks data tables',
      icon: <StorageIcon fontSize="large" />,
      action: () => navigate('/admin/quickbooks/tables'),
      color: '#0277bd',
    },
    {
      title: 'Audit Logs',
      description: 'View all accounting-related user activity and changes',
      icon: <HistoryIcon fontSize="large" />,
      action: () => navigate('/admin/hub/accounting/audits'),
      color: '#c62828',
    }] : []),
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Typography color="text.primary">Accounting Admin</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          Accounting Administration
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Manage subsidiary accounting data, user permissions, and QuickBooks integration
        </Typography>
      </Box>

      {/* Admin Functions */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Administration Functions
      </Typography>
      <Grid container spacing={3}>
        {adminSections.map((section) => (
          <Grid item xs={12} md={6} lg={4} key={section.title}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
              onClick={section.action}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      color: section.color,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {section.icon}
                  </Box>
                  <Typography variant="h6">
                    {section.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {section.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};